/**
 * Browser Notifications Utility
 * 
 * Handles desktop/browser push notifications for the application.
 * Requires user permission and browser support.
 */

/**
 * Check if browser supports notifications
 */
export const isBrowserNotificationSupported = () => {
  return 'Notification' in window;
};

/**
 * Get current notification permission status
 * @returns {'granted' | 'denied' | 'default'}
 */
export const getNotificationPermission = () => {
  if (!isBrowserNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
};

/**
 * Request notification permission from user
 * @returns {Promise<boolean>} true if granted, false otherwise
 */
export const requestNotificationPermission = async () => {
  if (!isBrowserNotificationSupported()) {
    console.log('❌ Browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    console.log('✅ Notification permission already granted');
    return true;
  }

  if (Notification.permission === 'denied') {
    console.log('❌ Notification permission denied');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    const granted = permission === 'granted';
    
    if (granted) {
      console.log('✅ Notification permission granted');
      // Save preference to localStorage
      localStorage.setItem('desktopNotifications', 'true');
    } else {
      console.log('❌ Notification permission denied by user');
      localStorage.setItem('desktopNotifications', 'false');
    }
    
    return granted;
  } catch (error) {
    console.error('❌ Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Show a browser notification
 * @param {string} title - Notification title
 * @param {Object} options - Notification options
 * @param {string} options.body - Notification body text
 * @param {string} options.icon - Icon URL
 * @param {string} options.badge - Badge URL
 * @param {string} options.tag - Unique tag to prevent duplicates
 * @param {boolean} options.requireInteraction - Keep notification visible until user interacts
 * @param {Function} options.onClick - Callback when notification is clicked
 * @returns {Notification|null} Notification instance or null if failed
 */
export const showBrowserNotification = (title, options = {}) => {
  // Check if notifications are supported
  if (!isBrowserNotificationSupported()) {
    console.log('❌ Browser notifications not supported');
    return null;
  }

  // Check permission
  if (Notification.permission !== 'granted') {
    console.log('❌ Notification permission not granted');
    return null;
  }

  // Check if user has enabled desktop notifications
  const desktopEnabled = localStorage.getItem('desktopNotifications');
  if (desktopEnabled === 'false') {
    console.log('ℹ️ Desktop notifications disabled by user');
    return null;
  }

  try {
    const {
      body = '',
      icon = '/logo192.png',
      badge = '/logo192.png',
      tag = 'notification-' + Date.now(),
      requireInteraction = false,
      onClick = null,
      ...restOptions
    } = options;

    // Create notification
    const notification = new Notification(title, {
      body,
      icon,
      badge,
      tag,
      requireInteraction,
      dir: 'auto', // Auto-detect text direction (RTL/LTR)
      ...restOptions
    });

    // Handle click event
    notification.onclick = (event) => {
      event.preventDefault(); // Prevent default browser behavior
      
      // Focus the window
      window.focus();
      
      // Call custom onClick handler if provided
      if (onClick && typeof onClick === 'function') {
        onClick(event);
      }
      
      // Close the notification
      notification.close();
    };

    // Auto-close after 10 seconds if not requireInteraction
    if (!requireInteraction) {
      setTimeout(() => {
        notification.close();
      }, 10000);
    }

    console.log('✅ Browser notification shown:', title);
    return notification;
  } catch (error) {
    console.error('❌ Error showing browser notification:', error);
    return null;
  }
};

/**
 * Show notification for deposit
 */
export const showDepositNotification = (amount, currency) => {
  return showBrowserNotification(
    'Deposit Successful',
    {
      body: `Your deposit of ${amount} ${currency} has been confirmed`,
      icon: '/logo192.png',
      tag: 'deposit-notification',
      onClick: () => {
        // Navigate to wallet page
        window.location.href = '/mywallet';
      }
    }
  );
};

/**
 * Show notification for withdrawal
 */
export const showWithdrawalNotification = (amount, currency) => {
  return showBrowserNotification(
    'Withdrawal Processed',
    {
      body: `Your withdrawal of ${amount} ${currency} has been processed`,
      icon: '/logo192.png',
      tag: 'withdrawal-notification',
      onClick: () => {
        // Navigate to wallet page
        window.location.href = '/mywallet';
      }
    }
  );
};

/**
 * Show notification for order
 */
export const showOrderNotification = (orderType, symbol, status) => {
  const title = status === 'filled' ? 'Order Filled' : 'Order Updated';
  const body = `Your ${orderType} order for ${symbol} has been ${status}`;
  
  return showBrowserNotification(
    title,
    {
      body,
      icon: '/logo192.png',
      tag: 'order-notification',
      onClick: () => {
        // Navigate to orders page
        window.location.href = '/orders';
      }
    }
  );
};

/**
 * Show generic notification from WebSocket message
 */
export const showGenericNotification = (title, content, section = null) => {
  return showBrowserNotification(
    title,
    {
      body: content,
      icon: '/logo192.png',
      tag: 'generic-notification-' + Date.now(),
      onClick: () => {
        // Navigate based on section
        if (section === 'WALLET') {
          window.location.href = '/mywallet';
        } else if (section === 'ORDER') {
          window.location.href = '/orders';
        } else if (section === 'TICKET') {
          window.location.href = '/ticket';
        } else {
          // Default: show notifications page
          window.location.href = '/notifications';
        }
      }
    }
  );
};

/**
 * Check if desktop notifications are enabled
 */
export const isDesktopNotificationsEnabled = () => {
  const enabled = localStorage.getItem('desktopNotifications');
  return enabled === 'true' && Notification.permission === 'granted';
};

/**
 * Enable desktop notifications
 */
export const enableDesktopNotifications = async () => {
  const granted = await requestNotificationPermission();
  if (granted) {
    localStorage.setItem('desktopNotifications', 'true');
  }
  return granted;
};

/**
 * Disable desktop notifications
 */
export const disableDesktopNotifications = () => {
  localStorage.setItem('desktopNotifications', 'false');
};

/**
 * Test notification (for settings page)
 */
export const showTestNotification = () => {
  return showBrowserNotification(
    'Test Notification',
    {
      body: 'Desktop notifications are working correctly!',
      icon: '/logo192.png',
      tag: 'test-notification'
    }
  );
};

